import { debounce } from "lodash-es"
import {
  useMemo,
  useCallback,
  useState,
  useRef,
} from "react"

// note time may also be "now" to signify scrolling to the end of the graph
interface ReturnValue {
  graphScrollToDate: Date;
  setGraphScrollToDate: (value: Date) => void;
  feedScrollToDate: Date;
  setFeedScrollToDate: (value: Date) => void;
  updateScrollController: (controller: "graph" | "feed" | undefined) => void;
}

const debounceTime = 250

export function useScrollState(): ReturnValue {
  const scrollControllerRef = useRef<"graph" | "feed" | undefined>(undefined)

  /*
   * when the user interacts with the graph or feed, that section controls
   * the scroll position of the other section
   */
  const updateScrollController = useCallback(
    (controller: "graph" | "feed" | undefined) => {
      scrollControllerRef.current = controller
    },
    [scrollControllerRef],
  )

  const [
    graphScrollToDate,
    setGraphScrollToDate,
  ] = useState<Date>(new Date())

  const [
    feedScrollToDate,
    setFeedScrollToDate,
  ] = useState<Date>(new Date())

  const debouncedSetGraphScrollToDate = useMemo(
    () => debounce(
      setGraphScrollToDate,
      debounceTime,
    ),
    [setGraphScrollToDate],
  )

  const debouncedSetFeedScrollToDate = useMemo(
    () => debounce(
      setFeedScrollToDate,
      debounceTime,
    ),
    [setFeedScrollToDate],
  )

  const setGraphScrollToDateOnUserAction = useCallback(
    (value: Date) => {
      const scrollController = scrollControllerRef.current
      if (scrollController === "feed") {
        debouncedSetGraphScrollToDate(value)
      }
    },
    [
      debouncedSetGraphScrollToDate,
      scrollControllerRef,
    ],
  )

  const setFeedScrollToDateOnUserAction = useCallback(
    (value: Date) => {
      const scrollController = scrollControllerRef.current
      if (scrollController === "graph") {
        debouncedSetFeedScrollToDate(value)
      }
    },
    [
      debouncedSetFeedScrollToDate,
      scrollControllerRef,
    ],
  )

  return {
    updateScrollController,
    graphScrollToDate,
    setGraphScrollToDate: setGraphScrollToDateOnUserAction,
    feedScrollToDate,
    setFeedScrollToDate: setFeedScrollToDateOnUserAction,
  }
}
